import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Modal, Form } from 'react-bootstrap';
import { useInventoryService } from 'services/inventory';
import { useInventorySourceService } from 'services/inventorySource';
import { useProductService } from 'services/products';
import { useApiClient } from 'hooks/useApiClient';
import { API_ROUTES, ROUTES } from 'utils/constants';
import { InventoryHistory, InventorySource } from 'types/inventory';
import Category from 'types/category';
import Button from 'components/Button';
import Toast from 'components/Toast';
import CategorySearchInput from 'components/CategorySearchInput';
import InventorySourceSearchInput from 'components/InventorySourceSearchInput';
import { ArrowLeft, Plus, Minus, History, AlertCircle } from 'lucide-react';
import styles from './Inventory.module.scss';

function ProductInventory() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const inventoryService = useInventoryService();
  const inventorySourceService = useInventorySourceService();
  const productService = useProductService();
  const apiClient = useApiClient();
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showHistory, setShowHistory] = useState(true);
  const [showNoSourcesModal, setShowNoSourcesModal] = useState(false);
  const [addQuantity, setAddQuantity] = useState('');
  const [addSourceCategory, setAddSourceCategory] = useState('');
  const [addCostPerUnit, setAddCostPerUnit] = useState('');
  const [addReason, setAddReason] = useState('');
  const [removeQuantity, setRemoveQuantity] = useState('');
  const [removeDestinationCategory, setRemoveDestinationCategory] =
    useState('');
  const [removeReason, setRemoveReason] = useState('');
  const [currentQuantity, setCurrentQuantity] = useState<string>('0');
  const [productName, setProductName] = useState('');
  const [categories, setCategories] = useState<{ [key: string]: Category }>({});
  const [inventorySources, setInventorySources] = useState<InventorySource[]>(
    [],
  );
  const [history, setHistory] = useState<InventoryHistory[]>([]);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  const showToast = useCallback(
    (message: string, type: 'success' | 'error') => {
      setToast({ message, type });
    },
    [],
  );

  // Load categories and inventory sources
  const loadCategories = useCallback(async () => {
    try {
      const [categoriesData, sourcesData] = await Promise.all([
        apiClient.get<{ [key: string]: Category }>(API_ROUTES.CATEGORY),
        inventorySourceService.getAllInventorySources(),
      ]);
      setCategories(categoriesData || {});
      setInventorySources(sourcesData);
    } catch (error) {
      console.error('Failed to load categories or inventory sources:', error);
    }
  }, [apiClient, inventorySourceService]);

  const loadHistory = useCallback(async () => {
    if (!id) return;

    try {
      const historyData = await inventoryService.getInventoryHistory(id);

      // First, sort by createdAt in ascending order (oldest first) to calculate remaining quantities
      const sortedChronologically = [...historyData].sort((a, b) => {
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        return dateA - dateB; // Ascending order (oldest first)
      });

      // Calculate remaining quantity for each entry chronologically
      let runningTotal = 0;
      const historyWithComputedRemaining = sortedChronologically.map(
        (entry) => {
          if (entry.isVoided) {
            // For voided entries, keep the original remainingQuantity
            return entry;
          }

          // Update running total based on change type
          if (entry.changeType === 'add') {
            runningTotal += Number(entry.quantityChange || 0);
          } else if (
            entry.changeType === 'remove' ||
            entry.changeType === 'sale'
          ) {
            runningTotal += Number(entry.quantityChange || 0); // quantityChange is already negative
          }

          // Return entry with computed remainingQuantity (convert to string)
          return {
            ...entry,
            remainingQuantity: runningTotal.toFixed(2),
          };
        },
      );

      // Sort by createdAt in descending order (newest first) for display
      const sortedHistory = historyWithComputedRemaining.sort((a, b) => {
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        return dateB - dateA; // Descending order (newest first)
      });

      setHistory(sortedHistory);

      // Calculate current quantity from the most recent non-voided entry's remainingQuantity
      const mostRecentNonVoided = sortedHistory.find(
        (entry) => !entry.isVoided,
      );
      const currentQuantity = mostRecentNonVoided
        ? Number(mostRecentNonVoided.remainingQuantity)
        : 0;
      setCurrentQuantity(currentQuantity.toFixed(2));
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to load inventory history.';
      showToast(errorMessage, 'error');
    }
  }, [id, inventoryService, showToast]);

  // Load product and inventory data
  useEffect(() => {
    if (!id) {
      showToast('Product ID is required', 'error');
      navigate(ROUTES.PRODUCTS.ROOT);
      return;
    }

    const loadData = async () => {
      setLoading(true);
      try {
        // Load categories
        await loadCategories();

        // Load product to get name and inventory
        const product = await productService.getProductById(id);
        setProductName(product.name);

        // Load history automatically on page load
        await loadHistory();
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to load inventory. Please try again.';
        showToast(errorMessage, 'error');
        navigate(ROUTES.PRODUCTS.ROOT);
      } finally {
        setLoading(false);
      }
    };

    void loadData();
  }, [id, productService, showToast, navigate, loadCategories, loadHistory]);

  // History is now loaded automatically on page load, so this effect is no longer needed

  const handleAddStock = useCallback(async () => {
    if (!id || !addQuantity) {
      showToast('Please enter a quantity', 'error');
      return;
    }

    if (!addSourceCategory) {
      showToast('Please select an inventory source', 'error');
      return;
    }

    const quantityNum = Number(addQuantity);
    if (isNaN(quantityNum) || quantityNum <= 0) {
      showToast('Quantity must be a positive number', 'error');
      return;
    }

    if (!addCostPerUnit) {
      showToast('Cost per unit is required', 'error');
      return;
    }

    const costPerUnitNum = Number(addCostPerUnit);
    if (isNaN(costPerUnitNum) || costPerUnitNum <= 0) {
      showToast('Cost per unit must be a positive number', 'error');
      return;
    }

    const selectedSource = inventorySources.find(
      (s) => s.id === addSourceCategory,
    );
    if (!selectedSource) {
      showToast('Please select a valid inventory source', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      await inventoryService.addStock(id, {
        quantity: quantityNum,
        sourceCategoryId: selectedSource.category.id,
        costPerUnit: costPerUnitNum,
        reason: addReason.trim() || undefined,
      });
      setAddQuantity('');
      setAddSourceCategory('');
      setAddCostPerUnit('');
      setAddReason('');
      setShowAddModal(false);
      showToast('Stock added successfully!', 'success');
      // Reload history to update current quantity
      void loadHistory();
      if (showHistory) {
        // History will be reloaded by loadHistory
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to add stock. Please try again.';
      showToast(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  }, [
    id,
    addQuantity,
    addSourceCategory,
    addCostPerUnit,
    addReason,
    inventoryService,
    inventorySources,
    showToast,
    showHistory,
    loadHistory,
  ]);

  const handleRemoveStock = useCallback(async () => {
    if (!id || !removeQuantity) {
      showToast('Please enter a quantity', 'error');
      return;
    }

    if (!removeDestinationCategory) {
      showToast('Please select a destination category', 'error');
      return;
    }

    const quantityNum = Number(removeQuantity);
    if (isNaN(quantityNum) || quantityNum <= 0) {
      showToast('Quantity must be a positive number', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      await inventoryService.removeStock(id, {
        quantity: quantityNum,
        destinationCategoryId: removeDestinationCategory,
        reason: removeReason.trim() || undefined,
      });
      setRemoveQuantity('');
      setRemoveDestinationCategory('');
      setRemoveReason('');
      setShowRemoveModal(false);
      showToast('Stock removed successfully!', 'success');
      // Reload history to update current quantity
      void loadHistory();
      if (showHistory) {
        // History will be reloaded by loadHistory
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to remove stock. Please try again.';
      showToast(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  }, [
    id,
    removeQuantity,
    removeDestinationCategory,
    removeReason,
    inventoryService,
    showToast,
    showHistory,
    loadHistory,
  ]);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading inventory...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className={styles.header}>
        <button
          onClick={() => {
            navigate(ROUTES.INVENTORY.ROOT);
          }}
          className={styles.backButton}
          aria-label="Back to inventory"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className={styles.title}>Inventory Management</h1>
          <p className={styles.subtitle}>{productName}</p>
        </div>
        <div className={styles.headerActions}>
          <Button
            variant="outline"
            onClick={() => {
              if (inventorySources.length === 0) {
                setShowNoSourcesModal(true);
              } else {
                setShowAddModal(true);
              }
            }}
          >
            <Plus size={18} />
            Add Stock
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              if (inventorySources.length === 0) {
                setShowNoSourcesModal(true);
              } else {
                setShowRemoveModal(true);
              }
            }}
          >
            <Minus size={18} />
            Remove Stock
          </Button>
        </div>
      </div>

      <div className={styles.content}>
        {/* Current Stock */}
        <div className={styles.card}>
          <h2 className={styles.cardTitle}>Current Stock</h2>
          <div className={styles.stockDisplay}>
            <span className={styles.stockQuantity}>{currentQuantity}</span>
            <span className={styles.stockLabel}>units in stock</span>
          </div>
        </div>

        {/* History */}
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <h2 className={styles.cardTitle}>Inventory History</h2>
            <Button
              variant="outline"
              onClick={() => {
                setShowHistory(!showHistory);
              }}
              className={styles.toggleButton}
            >
              <History size={18} />
              {showHistory ? 'Hide History' : 'Show History'}
            </Button>
          </div>

          {showHistory && (
            <div className={styles.history}>
              {history.length === 0 ? (
                <p className={styles.emptyHistory}>No history available</p>
              ) : (
                <div className={styles.historyList}>
                  {history.map((item: InventoryHistory) => {
                    // Format date safely
                    const formatDate = (dateString: string): string => {
                      try {
                        const date = new Date(dateString);
                        if (isNaN(date.getTime())) {
                          return 'Invalid Date';
                        }
                        return date.toLocaleString();
                      } catch {
                        return 'Invalid Date';
                      }
                    };

                    const quantityChangeNum = Number(item.quantityChange);
                    const isPositive = quantityChangeNum >= 0;

                    return (
                      <div
                        key={item.id}
                        className={`${styles.historyItem} ${styles.snapshotItem}`}
                      >
                        <div className={styles.historyHeader}>
                          <div className={styles.historyType}>
                            <History size={16} className={styles.historyIcon} />
                            <div className={styles.historyTypeContainer}>
                              <span className={styles.historyTypeText}>
                                {item.changeType === 'add'
                                  ? 'Stock Addition'
                                  : item.changeType === 'remove'
                                    ? 'Stock Removal'
                                    : item.changeType === 'sale'
                                      ? 'Sale'
                                      : 'Inventory Change'}
                              </span>
                              <span className={styles.historyDate}>
                                {formatDate(item.createdAt)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className={styles.historyDetails}>
                          <div className={styles.historyQuantity}>
                            <div className={styles.quantityRow}>
                              <span className={styles.quantityLabel}>
                                Original Quantity:
                              </span>
                              <span className={styles.quantityValue}>
                                {item.quantity} units
                              </span>
                            </div>
                            {item.quantityChange && (
                              <div className={styles.quantityRow}>
                                <span className={styles.quantityLabel}>
                                  Change:
                                </span>
                                <span
                                  className={`${styles.quantityChange} ${
                                    isPositive
                                      ? styles.positiveChange
                                      : styles.negativeChange
                                  }`}
                                >
                                  {isPositive ? '+' : ''}
                                  {item.quantityChange} units
                                </span>
                              </div>
                            )}
                            <div className={styles.quantityRow}>
                              <span className={styles.quantityLabel}>
                                Remaining Quantity:
                              </span>
                              <span className={styles.quantityValue}>
                                {item.remainingQuantity} units
                              </span>
                            </div>
                          </div>
                          {item.reason && (
                            <div className={styles.historyReason}>
                              <span className={styles.reasonLabel}>
                                Reason:
                              </span>
                              <span className={styles.reasonValue}>
                                {item.reason}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add Stock Modal */}
      <Modal
        show={showAddModal}
        onHide={() => {
          if (!isSubmitting) {
            setShowAddModal(false);
            setAddQuantity('');
            setAddSourceCategory('');
            setAddCostPerUnit('');
            setAddReason('');
          }
        }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Stock</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>
                Quantity <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0.01"
                value={addQuantity}
                onChange={(e) => setAddQuantity(e.target.value)}
                placeholder="0.00"
                disabled={isSubmitting}
              />
              <Form.Text className="text-muted">
                Amount of stock to add (must be positive)
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Inventory Source <span className="text-danger">*</span>
              </Form.Label>
              <InventorySourceSearchInput
                sources={inventorySources}
                value={addSourceCategory}
                onChange={setAddSourceCategory}
                placeholder="Search for inventory source (e.g., Cash Purchase, Supplier Credit)..."
                required
                disabled={isSubmitting}
              />
              <Form.Text className="text-muted">
                The inventory source to use (e.g., Cash Purchase, Supplier
                Credit). If a source is missing, ask a company admin to
                configure inventory sources for this workspace.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Cost Per Unit <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0.01"
                value={addCostPerUnit}
                onChange={(e) => setAddCostPerUnit(e.target.value)}
                placeholder="0.00"
                disabled={isSubmitting}
                required
              />
              <Form.Text className="text-muted">
                Cost per unit when adding inventory (required)
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Reason</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={addReason}
                onChange={(e) => setAddReason(e.target.value)}
                placeholder="e.g., Stock replenishment from supplier"
                disabled={isSubmitting}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowAddModal(false);
              setAddQuantity('');
              setAddSourceCategory('');
              setAddCostPerUnit('');
              setAddReason('');
            }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              void handleAddStock();
            }}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Adding...' : 'Add Stock'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Remove Stock Modal */}
      <Modal
        show={showRemoveModal}
        onHide={() => {
          if (!isSubmitting) {
            setShowRemoveModal(false);
            setRemoveQuantity('');
            setRemoveDestinationCategory('');
            setRemoveReason('');
          }
        }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Remove Stock</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>
                Quantity <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0.01"
                value={removeQuantity}
                onChange={(e) => setRemoveQuantity(e.target.value)}
                placeholder="0.00"
                disabled={isSubmitting}
              />
              <Form.Text className="text-muted">
                Amount of stock to remove (must be positive, uses FIFO)
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Destination Category <span className="text-danger">*</span>
              </Form.Label>
              <CategorySearchInput
                categories={categories}
                value={removeDestinationCategory}
                onChange={(category: Category) =>
                  setRemoveDestinationCategory(category?.id ?? '')
                }
                placeholder="Search for destination category (e.g., COGS, Expense)..."
                required
                disabled={isSubmitting}
                filter={(category) => !category.isVoided}
              />
              <Form.Text className="text-muted">
                The category to debit (e.g., COGS, Expense, Inventory Loss).
                Cost per unit is automatically calculated using FIFO from
                existing inventory entries.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Reason</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={removeReason}
                onChange={(e) => setRemoveReason(e.target.value)}
                placeholder="e.g., Sold to customer, Damaged items removed"
                disabled={isSubmitting}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowRemoveModal(false);
              setRemoveQuantity('');
              setRemoveDestinationCategory('');
              setRemoveReason('');
            }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              void handleRemoveStock();
            }}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Removing...' : 'Remove Stock'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* No Inventory Sources Modal */}
      <Modal
        show={showNoSourcesModal}
        onHide={() => setShowNoSourcesModal(false)}
        className={styles.modal}
        contentClassName={styles.modalContent}
        dialogClassName={styles.modalDialog}
        backdrop="static"
      >
        <Modal.Header closeButton className={styles.modalHeader}>
          <Modal.Title className={styles.modalTitle}>
            <AlertCircle size={20} style={{ marginRight: '0.5rem' }} />
            Inventory Sources Required
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.modalBody}>
          <div className={styles.noSourcesMessage}>
            <p>
              Before you can add or remove stock, you need to set up at least
              one inventory source.
            </p>
            <p>
              Inventory sources represent where your inventory comes from (e.g.,
              Cash Purchase, Supplier Credit, Capital Contribution). They are
              used to track the accounting category that will be credited when
              adding inventory.
            </p>
            <p>
              <strong>
                Ask a company admin to create inventory sources, then try adding
                or removing stock again.
              </strong>
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer className={styles.modalFooter}>
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowNoSourcesModal(false)}
          >
            OK
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default ProductInventory;
